import React, { useEffect, useRef, useState } from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from 'sonner';
import { playScanFeedback, warmupVoices } from '@/lib/scanFeedback';

export default function ScannerDrawer({ open, onOpenChange, onDetected, dedupeMs = 2500 }) {
  const regionId = 'qr-reader-region';
  const qrRef = useRef(null);
  const [manual, setManual] = useState('');
  const [camError, setCamError] = useState('');
  const [flash, setFlash] = useState('');
  // One physical scan = one onDetected. Guards against multi-frame camera
  // detections and rapid repeat scans of the same code.
  const lastScan = useRef({ code: '', ts: 0 });

  useEffect(() => {
    if (!open) return;
    // Close any on-screen keyboard so the camera/scanner is fully visible.
    try { document.activeElement && document.activeElement.blur && document.activeElement.blur(); } catch (e) { /* ignore */ }
    warmupVoices();
    setCamError('');
    lastScan.current = { code: '', ts: 0 };
    let qr;
    const t = setTimeout(async () => {
      try {
        qr = new Html5Qrcode(regionId, { verbose: false });
        qrRef.current = qr;
        await qr.start(
          { facingMode: 'environment' },
          { fps: 12, qrbox: { width: 240, height: 150 } },
          (decodedText) => { handleDetected(decodedText); },
          () => {}
        );
      } catch (e) {
        setCamError('Camera not available. Use manual entry below.');
      }
    }, 250);
    return () => {
      clearTimeout(t);
      if (qrRef.current) {
        qrRef.current.stop().then(() => qrRef.current.clear()).catch(() => {});
        qrRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleDetected = (code) => {
    const clean = String(code || '').trim().toUpperCase();
    if (!clean) return;
    const now = Date.now();
    const last = lastScan.current;
    // Ignore repeated detections of the SAME code within the debounce window
    // (camera emits many frames per second for one physical barcode).
    if (last.code === clean && now - last.ts < dedupeMs) return;
    // Also enforce a short global lock so two different rapid frames can't
    // both fire before the consumer processes the first.
    if (now - last.ts < 350) return;
    lastScan.current = { code: clean, ts: now };
    playScanFeedback();
    setFlash(clean);
    setTimeout(() => setFlash(''), 700);
    onDetected(clean);
  };

  const submitManual = () => {
    if (!manual.trim()) return;
    handleDetected(manual.trim());
    setManual('');
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="bg-card">
        <div className="mx-auto my-2 h-1 w-10 rounded-full bg-border" />
        <DrawerHeader>
          <DrawerTitle data-testid="scanner-title">Scan barcode</DrawerTitle>
        </DrawerHeader>
        <div className="px-4 pb-6">
          <div id={regionId} className="overflow-hidden rounded-2xl border border-border bg-black/5 min-h-[180px]" />
          {flash && (
            <div className="mt-2 flex items-center justify-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800" data-testid="scanner-flash">
              <span>✓ Scanned</span><span className="font-mono">{flash}</span>
            </div>
          )}
          {camError && <p className="mt-2 text-xs text-amber-700">{camError}</p>}
          <div className="mt-4">
            <label className="text-xs font-medium text-muted-foreground">Or enter code manually</label>
            <div className="mt-1 flex gap-2">
              <Input data-testid="scanner-manual-input" value={manual}
                onChange={(e) => setManual(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitManual()}
                placeholder="e.g. KT001" className="h-11 rounded-xl uppercase" autoFocus />
              <Button data-testid="scanner-manual-add" onClick={submitManual} className="h-11 rounded-xl">Add</Button>
            </div>
          </div>
          <Button variant="ghost" className="mt-3 w-full" data-testid="scanner-close"
            onClick={() => onOpenChange(false)}>Close scanner</Button>
          <p className="mt-2 text-center text-xs text-muted-foreground">Keep the barcode inside the frame. Works offline.</p>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
